import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { Users } from "lucide-react";
import { Card, Notice, PanelSection } from "./shared";
import { Badge, Spinner } from "../ui";
import { api } from "../../lib/api";
import type { LedgerGridRow } from "./StudentsDataGrid";

// Committee-only vote ledger: which student voted for what. The roster is a
// themed MUI X DataGrid (lazy-loaded — the heavy MUI code stays out of the
// public bundle and is fetched only on this admin screen). Clicking a row shows
// that student's ballots below. Individual votes are never public.
const StudentsDataGrid = lazy(() => import("./StudentsDataGrid"));

export default function StudentsPanel() {
  const [rows, setRows] = useState<LedgerGridRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<LedgerGridRow | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await api.adminLedger();
      setRows(res.students);
    } catch {
      setError("Could not load the student ledger.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <PanelSection
        title="Students & vote ledger"
        description="Every registered student and exactly what they voted for. Contact, email, transport and food preference are committee-only — this data is never shown on the public site. Sort, filter and resize columns; click a row to see that student's ballots."
      >
        {error && <Notice tone="error">{error}</Notice>}
        {!rows ? (
          <Spinner label="Loading ledger" />
        ) : rows.length === 0 ? (
          <Card className="p-6">
            <p className="text-white/60">
              No students registered yet. Students appear here as soon as they sign up on the voting
              page.
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-white/60">
              <Users className="h-4 w-4 text-wandor-prompt" aria-hidden="true" />
              {rows.length} registered student{rows.length === 1 ? "" : "s"} ·{" "}
              {rows.filter((r) => r.ballots.length > 0).length} have voted ·{" "}
              {rows.filter((r) => r.dietary === "veg").length} veg ·{" "}
              {rows.filter((r) => r.dietary === "non-veg").length} non-veg ·{" "}
              {rows.filter((r) => r.dietary === "both").length} both ·{" "}
              {rows.filter((r) => r.canPickupFriends === true).length} can give pickups
            </div>

            <Suspense fallback={<Spinner label="Loading table" />}>
              <StudentsDataGrid rows={rows} onSelect={setSelected} />
            </Suspense>

            {selected && (
              <Card className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-lg font-medium text-white">{selected.fullName}</p>
                    <p className="mt-0.5 text-sm text-white/55">
                      Faculty {selected.facultyNumber} · Enrollment {selected.enrollmentNumber} ·
                      Semester {selected.semester}
                    </p>
                    <p className="mt-0.5 text-xs text-white/45">
                      Contact {selected.contactNumber} · {selected.email} · Food:{" "}
                      {selected.dietary ?? "—"} · Transport:{" "}
                      {selected.hasTransport == null ? "—" : selected.hasTransport ? "Yes" : "No"}
                      {selected.hasTransport
                        ? ` · Pickup: ${selected.canPickupFriends == null ? "—" : selected.canPickupFriends ? "Yes" : "No"}`
                        : ""}
                    </p>
                  </div>
                  <Badge tone={selected.ballots.length > 0 ? "success" : "neutral"}>
                    {selected.ballots.length > 0
                      ? `${selected.ballots.length} ballot${selected.ballots.length === 1 ? "" : "s"}`
                      : "not voted yet"}
                  </Badge>
                </div>
                {selected.ballots.length > 0 ? (
                  <div className="mt-4 space-y-2 border-t border-white/10 pt-4">
                    {selected.ballots.map((b, i) => (
                      <div key={`${selected.id}-${i}`} className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                        <span className="text-white/65">
                          <span className="text-white/40">[{b.category}]</span> {b.pollTitle}
                        </span>
                        <span className="font-medium text-white">{b.selected.join(", ")}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 border-t border-white/10 pt-4 text-sm text-white/50">
                    This student has not cast any ballots yet.
                  </p>
                )}
              </Card>
            )}
          </div>
        )}
      </PanelSection>
    </div>
  );
}
